class SolenaMobileMenu extends HTMLElement {
  connectedCallback() {
    if (this.abortController) return;

    this.abortController = new AbortController();
    this.drawer = this.querySelector('#solena-drawer');
    this.closeButton = this.querySelector('.solena-drawer-close');
    this.mobileQuery = window.matchMedia('(max-width: 760px)');
    const { signal } = this.abortController;

    document.addEventListener('click', (event) => this.onClick(event), { signal });
    document.addEventListener('keydown', (event) => this.onKeydown(event), { signal });
    document.addEventListener('shopify:section:load', () => this.scheduleButtons(), { signal });
    document.addEventListener('cart:update', () => this.scheduleCartBadgeRefresh(), { signal });
    this.mobileQuery.addEventListener('change', () => {
      if (!this.mobileQuery.matches) this.setOpen(false, { restoreFocus: false });
    }, { signal });

    this.ensureButtons();
  }

  disconnectedCallback() {
    this.setOpen(false, { restoreFocus: false });
    this.abortController?.abort();
    window.cancelAnimationFrame(this.buttonFrame);
    window.clearTimeout(this.cartBadgeTimer);
    document.querySelectorAll('[data-solena-menu-button]').forEach((button) => button.remove());
    this.abortController = null;
  }

  onClick(event) {
    if (!(event.target instanceof Element)) return;

    const opener = event.target.closest('[data-solena-menu-button]');
    if (opener) {
      event.preventDefault();
      this.setOpen(true, { opener });
      return;
    }

    if (event.target.closest('.solena-drawer-close') || event.target.classList.contains('solena-drawer-overlay')) {
      this.setOpen(false);
      return;
    }

    if (event.target.closest('#solena-drawer a')) this.setOpen(false);
  }

  onKeydown(event) {
    if (!this.isOpen()) return;

    if (event.key === 'Escape') {
      event.preventDefault();
      this.setOpen(false);
      return;
    }

    if (event.key !== 'Tab') return;

    const focusable = this.getFocusableElements();
    if (!focusable.length) {
      event.preventDefault();
      this.closeButton?.focus();
      return;
    }

    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }

  ensureButtons() {
    document.querySelectorAll('.tx-hi, .snt-in').forEach((header) => {
      if (header.querySelector('[data-solena-menu-button]')) return;

      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'solena-menu-btn';
      button.dataset.solenaMenuButton = '';
      button.setAttribute('aria-label', this.dataset.openLabel);
      button.setAttribute('aria-controls', 'solena-drawer');
      button.setAttribute('aria-expanded', 'false');
      button.innerHTML = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" aria-hidden="true"><path d="M4 7h16M4 12h16M4 17h16"/></svg>';
      header.prepend(button);
    });
  }

  scheduleButtons() {
    window.cancelAnimationFrame(this.buttonFrame);
    this.buttonFrame = window.requestAnimationFrame(() => this.ensureButtons());
  }

  setOpen(open, { opener, restoreFocus = true } = {}) {
    if (!this.drawer || (open && !this.mobileQuery.matches)) return;

    if (open) this.previouslyFocused = opener || document.activeElement;
    document.body.classList.toggle('solena-menu-open', open);
    this.drawer.setAttribute('aria-hidden', String(!open));
    this.drawer.toggleAttribute('inert', !open);
    document.querySelectorAll('[data-solena-menu-button]').forEach((button) => {
      button.setAttribute('aria-expanded', String(open));
    });

    if (open) {
      window.requestAnimationFrame(() => this.closeButton?.focus());
    } else if (restoreFocus && this.previouslyFocused?.isConnected) {
      this.previouslyFocused.focus();
      this.previouslyFocused = null;
    }
  }

  isOpen() {
    return document.body.classList.contains('solena-menu-open');
  }

  getFocusableElements() {
    if (!this.drawer) return [];
    return [...this.drawer.querySelectorAll('a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])')];
  }

  updateCartBadge(itemCount) {
    const count = Number(itemCount);
    if (!Number.isFinite(count)) return;

    document.querySelectorAll('.snt-cart').forEach((cartLink) => {
      let badge = cartLink.querySelector('em');
      if (count > 0) {
        if (!badge) {
          badge = document.createElement('em');
          cartLink.appendChild(badge);
        }
        badge.textContent = count;
      } else {
        badge?.remove();
      }
    });
  }

  scheduleCartBadgeRefresh() {
    window.clearTimeout(this.cartBadgeTimer);
    this.cartBadgeTimer = window.setTimeout(async () => {
      try {
        const response = await fetch(this.dataset.cartUrl, {
          headers: { Accept: 'application/json' },
          signal: this.abortController.signal
        });
        if (!response.ok) return;
        const cart = await response.json();
        this.updateCartBadge(cart.item_count);
      } catch (error) {
        if (error.name !== 'AbortError') return;
      }
    }, 0);
  }
}

if (!customElements.get('solena-mobile-menu')) {
  customElements.define('solena-mobile-menu', SolenaMobileMenu);
}
